import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  History,
  TrendingUp,
  Box,
  Settings2,
  FileText,
  WalletCards,
  Clock3,
  CalendarClock,
  BriefcaseBusiness,
  ShieldCheck,
  LogOut,
  Menu,
  Sparkles,
  X
} from 'lucide-react';
import useAuthStore from '../store/useAuthStore';
import {
  canBrowseSupplyHubMarketplace,
  canSearchDoctorCircleProfiles,
  hasAttendanceAccess,
  hasFinanceRole,
  isPlatformAdminRole,
} from '../utils/rolePermissions';
import DektalAssistantWidget from './DektalAssistantWidget';

function getRoleLabel(role) {
  const normalized = String(role || '').trim().toLowerCase();
  if (['doctor_admin', 'owner_admin', 'clinic_owner', 'owner', 'admin'].includes(normalized)) {
    return 'Clinic Admin';
  }
  if (normalized === 'platform_admin') return 'Platform Admin';
  if (normalized === 'accountant') return 'Accountant';
  if (normalized === 'staff') return 'Staff';
  if (normalized === 'consultant_doctor' || normalized === 'consultant') return 'Consultant Doctor';
  if (normalized === 'doctor') return 'Doctor';
  return 'Clinic User';
}

function getInitials(primary, fallback) {
  const value = String(primary || fallback || 'Dektal Clinic').trim();
  const words = value
    .replace(/[^a-zA-Z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

  if (!words.length) return 'DC';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}

export default function Layout({ children }) {
  const { logout, user } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const canAccessFinanceMenus = hasFinanceRole(user?.role);
  const canAccessAttendance = hasAttendanceAccess(user?.role);
  const canAccessDoctorCircle = canSearchDoctorCircleProfiles(user?.role);
  const canAccessSupplyHub = canBrowseSupplyHubMarketplace(user?.role);
  const canAccessBusinessControl = isPlatformAdminRole(user?.role);
  const capabilities = new Set(user?.capabilities || []);
  const hasCapability = (capability, legacyFallback = false) => Array.isArray(user?.capabilities) ? capabilities.has(capability) : legacyFallback;

  const menuItems = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    ...(hasCapability('clinical.view', true) ? [{ name: 'Patient History', icon: History, path: '/patients' }] : []),
    ...(hasCapability('inventory.view', true) ? [{ name: 'Inventory', icon: Box, path: '/inventory' }] : []),
    ...(hasCapability('finance.profitability.view', canAccessFinanceMenus) ? [{ name: 'Pricing Engine', icon: TrendingUp, path: '/pricing' }] : []),
    ...(hasCapability('finance.view', canAccessFinanceMenus) ? [{ name: 'Lab & Consultant', icon: WalletCards, path: '/lab-consultant-ledger' }] : []),
    ...(hasCapability('billing.invoice.create') ? [{ name: 'Invoices', icon: WalletCards, path: '/print-documents?tab=invoice' }] : []),
    ...(hasCapability('finance.view', canAccessFinanceMenus) ? [{ name: 'Monthly Reports', icon: FileText, path: '/monthly-reports' }] : []),
    ...(hasCapability('finance.view', canAccessFinanceMenus) ? [{ name: 'Business Reports', icon: FileText, path: '/reports' }] : []),
    ...(canAccessAttendance ? [{ name: 'Staff Attendance', icon: Clock3, path: '/team-attendance' }] : []),
    ...(hasCapability('appointment.schedule', true) ? [{ name: 'Follow-ups', icon: CalendarClock, path: '/follow-ups' }] : []),
    ...(canAccessDoctorCircle ? [{ name: "Doctor's Circle", icon: BriefcaseBusiness, path: '/doctors-circle' }] : []),
    ...(canAccessSupplyHub ? [{ name: 'Supplies', icon: Box, path: '/supply-hub' }] : []),
    { name: 'Ask Dektal', icon: Sparkles, actionKey: 'askDektal' },
    ...(hasCapability('admin.profile.manage', false) ? [{ name: 'Clinic Workflow', icon: ShieldCheck, path: '/clinic-workflow' }] : []),
    { name: 'Settings', icon: Settings2, path: '/setup' },
    ...(canAccessBusinessControl ? [{ name: 'Business Control', icon: ShieldCheck, path: '/platform-admin/business-control' }] : []),
  ];
  const clinicName = user?.tenant?.clinicName || user?.clinicName || user?.tenant?.doctorName || '';
  const displayName = user?.name || user?.leadDoctor || user?.tenant?.doctorName || clinicName || 'Doctor';
  const roleLabel = getRoleLabel(user?.role);
  const avatarInitials = getInitials(clinicName || displayName, displayName);
  const clinicLogo = user?.tenant?.clinicLogo || user?.clinicLogo || '';
  const isActiveRoute = (item) => Boolean(item.path) && (
    location.pathname === item.path ||
    (item.path !== '/dashboard' && location.pathname.startsWith(`${item.path}/`))
  );
  const preferredMobilePaths = new Set(['/dashboard', '/patients', '/pricing', '/inventory']);
  const routeMenuItems = menuItems.filter((item) => item.path);
  const preferredMobileItems = routeMenuItems.filter((item) => preferredMobilePaths.has(item.path));
  const bottomNavItems = (preferredMobileItems.length >= 4 ? preferredMobileItems : routeMenuItems).slice(0, 4);
  const bottomNavPathSet = new Set(bottomNavItems.map((item) => item.path));
  const moreMenuActive = menuItems.some((item) => !bottomNavPathSet.has(item.path) && isActiveRoute(item));
  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    navigate('/login');
  };
  const handleRestoreAskDektal = () => {
    window.sessionStorage.removeItem('dektalAssistantHidden');
    window.dispatchEvent(new Event('dektal-assistant-restore'));
    setMobileMenuOpen(false);
  };

  return (
    <div className="flex h-screen max-w-full overflow-hidden bg-[#F8FAFC] font-sans">
      {/* PREMIUM SIDEBAR (Matches Visual Reference) */}
      <aside className="z-20 hidden w-[280px] shrink-0 flex-col bg-[#0F172A] text-white shadow-2xl lg:flex">

        <div className="p-6">
          {/* CLINIC OS GRADIENT CARD */}
          <div className="bg-gradient-to-br from-indigo-600 to-blue-600 rounded-3xl p-6 shadow-lg shadow-indigo-500/20 mb-4">
            <h1 className="text-2xl font-black text-white tracking-tight leading-none mb-3">
              Dektal 360 Pro
            </h1>
            <p className="text-xs text-indigo-100 font-medium leading-relaxed">
              Smart dental clinic operations, pricing, inventory, and reporting.
            </p>
          </div>
        </div>

        <nav className="flex-1 px-4 space-y-1.5 overflow-y-auto">
          {menuItems.map((item) => {
            const isActive = isActiveRoute(item);
            const itemClassName = `flex w-full items-center gap-3.5 px-5 py-3.5 rounded-2xl transition-all duration-200 ${
              isActive
                ? 'bg-blue-600 text-white font-bold shadow-md shadow-blue-900/50'
                : 'text-slate-400 hover:bg-white/5 hover:text-white font-semibold'
            }`;
            if (item.actionKey === 'askDektal') {
              return (
                <button key={item.name} type="button" onClick={handleRestoreAskDektal} className={itemClassName}>
                  <item.icon className="w-5 h-5" />
                  <span className="text-sm">{item.name}</span>
                </button>
              );
            }
            return (
              <Link
                key={item.name}
                to={item.path}
                className={itemClassName}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-sm">{item.name}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-6 mt-auto">
          <div className="mb-5 rounded-2xl border border-white/10 bg-white/[0.035] p-2.5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white/10 bg-slate-700 text-sm font-semibold text-white">
                {clinicLogo ? (
                  <img src={clinicLogo} alt={clinicName || displayName} className="h-full w-full object-cover" />
                ) : (
                  avatarInitials
                )}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-white" title={displayName}>
                  {displayName}
                </p>
                <p className="truncate text-[11px] font-medium text-slate-300" title={clinicName || 'Clinic workspace'}>
                  {clinicName || 'Clinic workspace'}
                </p>
                <p className="mt-0.5 truncate text-[10px] text-slate-500 uppercase font-semibold tracking-widest">
                  {roleLabel}
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-5 py-3 rounded-2xl text-slate-400 hover:bg-red-500/10 hover:text-red-500 transition-all font-bold text-sm"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* MAIN CONTENT AREA */}
      <main className="relative flex-1 overflow-y-auto bg-[#F8FAFC] pb-[6.5rem] lg:pb-0">
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 px-4 py-3 shadow-sm backdrop-blur lg:hidden">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-blue-600 text-sm font-bold text-white">
                {clinicLogo ? (
                  <img src={clinicLogo} alt={clinicName || displayName} className="h-full w-full object-cover" />
                ) : (
                  avatarInitials
                )}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-slate-900">{clinicName || 'Dektal 360 Pro'}</p>
                <p className="truncate text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                  {roleLabel}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 shadow-sm"
              aria-label="Open navigation menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </header>

        {children}
        <DektalAssistantWidget />
      </main>

      {mobileMenuOpen ? (
        <div className="fixed inset-0 z-[70] lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close navigation menu"
          />
          <div className="absolute bottom-0 left-0 right-0 max-h-[88dvh] overflow-hidden rounded-t-[28px] bg-[#0F172A] text-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-white/10 p-5">
              <div className="min-w-0">
                <h2 className="mt-1 truncate text-xl font-black tracking-tight">Dektal 360 Pro</h2>
                <p className="mt-2 truncate text-xs font-medium text-slate-300">{clinicName || displayName}</p>
              </div>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-slate-200"
                aria-label="Close navigation menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="max-h-[54dvh] space-y-1 overflow-y-auto px-4 py-4">
              {menuItems.map((item) => {
                const isActive = isActiveRoute(item);
                const itemClassName = `flex min-h-12 w-full items-center gap-3 rounded-2xl px-4 py-3 text-left transition ${
                  isActive
                    ? 'bg-blue-600 text-white shadow-md shadow-blue-950/40'
                    : 'text-slate-300 hover:bg-white/5 hover:text-white'
                }`;
                if (item.actionKey === 'askDektal') {
                  return (
                    <button key={item.name} type="button" onClick={handleRestoreAskDektal} className={itemClassName}>
                      <item.icon className="h-5 w-5 shrink-0" />
                      <span className="text-sm font-bold">{item.name}</span>
                    </button>
                  );
                }
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={itemClassName}
                  >
                    <item.icon className="h-5 w-5 shrink-0" />
                    <span className="text-sm font-bold">{item.name}</span>
                  </Link>
                );
              })}
            </nav>

            <div className="border-t border-white/10 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
              <div className="mb-3 rounded-2xl border border-white/10 bg-white/[0.035] p-3">
                <p className="truncate text-sm font-semibold text-white">{displayName}</p>
                <p className="mt-1 truncate text-[11px] font-medium text-slate-300">{clinicName || 'Clinic workspace'}</p>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm font-bold text-red-200"
              >
                <LogOut className="h-5 w-5" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-2 pb-[calc(0.45rem+env(safe-area-inset-bottom))] pt-2 shadow-[0_-14px_32px_rgba(15,23,42,0.12)] backdrop-blur lg:hidden">
        <div className="mx-auto grid max-w-md grid-cols-5 gap-1">
          {bottomNavItems.map((item) => {
            const isActive = isActiveRoute(item);
            return (
              <Link
                key={item.name}
                to={item.path}
                className={`flex min-h-[58px] flex-col items-center justify-center gap-1 rounded-2xl px-1 text-center transition ${
                  isActive ? 'bg-blue-50 text-blue-700' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                }`}
              >
                <item.icon className="h-5 w-5" />
                <span className="max-w-full truncate text-[10px] font-bold leading-tight">{item.name}</span>
              </Link>
            );
          })}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className={`flex min-h-[58px] flex-col items-center justify-center gap-1 rounded-2xl px-1 text-center transition ${
              moreMenuActive ? 'bg-blue-50 text-blue-700' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
            }`}
          >
            <Menu className="h-5 w-5" />
            <span className="text-[10px] font-bold leading-tight">More</span>
          </button>
        </div>
      </nav>
    </div>
  );
}
